import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Check, Mail, Trophy, Sparkles, Lock, Unlock, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Refer = () => {
  const [email, setEmail] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Auto-load if email is stored locally (returning visitor)
  useEffect(() => {
    const stored = localStorage.getItem('subscriber_email');
    if (stored) {
      setEmail(stored);
      load(stored);
    }
  }, []);

  const load = async (e) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/referrals/me`, { params: { email: e } });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Not subscribed yet — subscribe first.');
        setData(null);
      } else {
        toast.error('Could not load referral stats');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v.includes('@')) { toast.error('Enter a valid email'); return; }
    localStorage.setItem('subscriber_email', v);
    load(v);
  };

  const copyShareUrl = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error('Copy failed'); }
  };

  const shareNative = (platform) => {
    const text = `I'm following @riddhitikoo's free AI ethics blog. Subscribe and get free guides on Claude Code, ChatGPT prompting, and the hottest AI models 👇`;
    const url = data?.share_url || '';
    let intent = '';
    if (platform === 'twitter') intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + '\n\n' + url)}`;
    if (platform === 'linkedin') intent = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    if (platform === 'whatsapp') intent = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    if (platform === 'email') intent = `mailto:?subject=${encodeURIComponent('Free AI ethics lessons (worth a look)')}&body=${encodeURIComponent(text + '\n\n' + url)}`;
    if (intent) window.open(intent, '_blank', 'noopener,noreferrer');
  };

  const count = data?.count || 0;
  const next = data?.next_milestone;
  const progressPct = next ? Math.min(100, (count / next.count) * 100) : 100;

  return (
    <div className="min-h-screen bg-white" data-testid="refer-page">
      <SEO
        title="Refer Friends · Unlock Bonus Guides"
        description="Share AI Banter with Riddhi and unlock bonus guides at every milestone — Claude Code Tips, ChatGPT Prompting Playbook, Hot AI Models, and more."
        path="/refer"
      />
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-semibold tracking-wider uppercase mb-4">Refer & unlock</span>
          <h1 className="text-5xl md:text-6xl font-light mb-5 tracking-tight">Bring a friend.<br/>Get bonus guides.</h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
            Every friend who subscribes through your link unlocks more for both of you — no spam, no fluff, just better AI literacy in their inbox.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          {/* Email gate */}
          {!data ? (
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-10">
              <h2 className="text-2xl font-medium mb-2">Enter your subscriber email</h2>
              <p className="text-gray-600 mb-6">
                Already subscribed? Drop your email below to get your unique share link. Not yet?{' '}
                <Link to="/" className="text-black font-medium underline">Subscribe first</Link>.
              </p>
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    data-testid="refer-email-input"
                  />
                </div>
                <button type="submit" disabled={loading} className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="refer-submit-btn">
                  {loading ? 'Loading…' : 'Get my link'}
                </button>
              </form>
            </div>
          ) : (
            <>
              {/* Stats card */}
              <div className="bg-gradient-to-br from-black to-gray-800 text-white rounded-2xl p-8 mb-8 relative overflow-hidden" data-testid="refer-stats">
                <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Trophy size={28} className="text-amber-400" />
                      <div>
                        <div className="text-sm text-gray-300">Friends invited</div>
                        <div className="text-5xl font-light tabular-nums">{count}</div>
                      </div>
                    </div>
                    {data.achieved.length > 0 ? (
                      <div className="text-right">
                        <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Unlocked</div>
                        <div className="text-sm">{data.achieved.length} reward{data.achieved.length === 1 ? '' : 's'}</div>
                      </div>
                    ) : null}
                  </div>
                  {next ? (
                    <>
                      <div className="flex justify-between text-xs text-gray-300 mb-2">
                        <span>Next: {next.label}</span>
                        <span className="tabular-nums">{count} / {next.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-amber-400 to-amber-300 transition-all duration-700" style={{ width: `${progressPct}%` }} />
                      </div>
                      <p className="text-xs text-gray-400 mt-3">{next.count - count} more friend{next.count - count === 1 ? '' : 's'} unlocks {next.label}.</p>
                    </>
                  ) : (
                    <p className="text-amber-300">All milestones unlocked. You're a legend.</p>
                  )}
                </div>
              </div>

              {/* Share link */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-8">
                <h3 className="text-base font-semibold uppercase tracking-wider text-gray-500 mb-3">Your share link</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    readOnly
                    value={data.share_url}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-md font-mono text-sm bg-gray-50"
                    data-testid="refer-share-url"
                    onClick={(e) => e.target.select()}
                  />
                  <button onClick={copyShareUrl} className="px-5 py-3 bg-black text-white rounded-md hover:bg-gray-800 inline-flex items-center justify-center gap-2" data-testid="refer-copy-btn">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    <span>{copied ? 'Copied' : 'Copy link'}</span>
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  <button onClick={() => shareNative('twitter')} className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="refer-share-twitter">Tweet</button>
                  <button onClick={() => shareNative('linkedin')} className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="refer-share-linkedin">LinkedIn</button>
                  <button onClick={() => shareNative('whatsapp')} className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="refer-share-whatsapp">WhatsApp</button>
                  <button onClick={() => shareNative('email')} className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50" data-testid="refer-share-email">Email</button>
                </div>
              </div>

              {/* Milestones */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="text-base font-semibold uppercase tracking-wider text-gray-500 mb-4">Milestones</h3>
                <ul className="space-y-3">
                  {data.milestones.map((m) => {
                    const unlocked = count >= m.count;
                    return (
                      <li key={m.count} className={`flex items-center gap-4 p-4 rounded-lg border ${unlocked ? 'bg-emerald-50/40 border-emerald-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`milestone-${m.count}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${unlocked ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                          {unlocked ? <Unlock size={18} /> : <Lock size={18} />}
                        </div>
                        <div className="flex-1">
                          <div className={`font-medium ${unlocked ? 'text-emerald-900' : 'text-gray-800'}`}>{m.count} friends → {m.label}</div>
                          {unlocked && m.unlock ? (
                            <Link to={`/guides/${m.unlock}`} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1 mt-1">
                              Open guide <ArrowRight size={12} />
                            </Link>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <button onClick={() => { setData(null); setEmail(''); localStorage.removeItem('subscriber_email'); }} className="text-xs text-gray-500 hover:text-black mt-6 mx-auto block">
                Switch subscriber email
              </button>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Refer;
